import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import Paper from '@mui/material/Paper'
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Tooltip,
  IconButton,
  Box,
  lighten,
  useTheme
} from '@mui/material'
import { Add } from '@mui/icons-material'
import { addObject, deleteObject, editObject } from '../../../api/apiClientCrud'
import MyTableCellHeader from './MyTableCellHeader'
import MyTableEditableRow from './MyTableEditableRow'
import MyTablePaginationActions from './MyTablePaginationActions'
import { useTableSort } from '../../hooks/useTableSort'
import { handleImagesUpload } from '../../../utils/documentManager'
import { useAuth } from '../../../context/AuthContext'
import { toast } from 'react-toastify'

function MyTableEditable({ mode = 'edit', data, setData, field, tableOptions, activePage = 0 }) {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [dataSet, setDataSet] = useState(() => {
    return data?.[field?.name] || []
  })
  const [editMode, setEditMode] = useState(false)
  const [rowId, setRowId] = useState(null)
  const [editingRow, setEditingRow] = useState({})
  const [previousRow, setPreviousRow] = useState({})
  const [isSubmit, setIsSubmit] = useState(false)
  const [isChanged, setIsChanged] = useState(false)
  const [isJustSaved, setIsJustSaved] = useState(false)

  const childRef = useRef([])
  const theme = useTheme()
  const { user } = useAuth()

  // Refs pour optimisation
  const opVersionRef = useRef(0)
  const initializedRef = useRef(false)
  const scrollContainerRef = useRef(null)
  const scrollPositionRef = useRef(0)
  const isPristineRef = useRef(true) // true tant que l'utilisateur n'a pas édité
  const isUserScrollingRef = useRef(false) // Détecter si l'utilisateur scroll manuellement
  const syncTimeoutRef = useRef(null) // Pour debouncer la synchronisation
  const dataSetRef = useRef(data?.[field?.name] || []) // Miroir de dataSet, toujours à jour
  const dataRef = useRef(data) // Miroir de data, pour éviter les closures stales

  // Mettre à jour dataRef à chaque render pour éviter les closures stales
  dataRef.current = data

  const WHITE_COLOR = '#FFFFFF'
  const ODD_ROW_COLOR = lighten(theme.palette.primary.main, 0.85)

  const { sortedData, sortedColumn, sortedColumnMode, handleSort } = useTableSort(dataSet, tableOptions.columns)

  const headerStyle = { height: '30px', padding: '10px' }
  const paperStyle = tableOptions.paperStyle || { width: '90%', marginLeft: '5%' }
  const tableStyle = useMemo(
    () => tableOptions?.tableStyle || { maxHeight: '100%', height: '100%', overflowY: 'auto' },
    [tableOptions]
  )

  // Helper: detect if row is empty (customizable via tableOptions)
  const isEmptyRow = useCallback(
    (r) => {
      if (tableOptions.isEmptyRow) {
        return tableOptions.isEmptyRow(r)
      }

      // Default logic
      if (!r) return true
      if (Array.isArray(r.attachments) && r.attachments.length) return false
      const keys = Object.keys(r || {})
      if (keys.length === 0) return true
      for (const k of keys) {
        const v = r[k]
        if (v !== undefined && v !== null && String(v).trim() !== '') return false
      }
      return true
    },
    [tableOptions]
  )

  // Sauvegarder la position de scroll CONTINUELLEMENT pendant l'édition
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => {
      if (editMode) {
        scrollPositionRef.current = container.scrollTop
        isUserScrollingRef.current = true
      }
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [editMode])

  // Restaurer la position UNIQUEMENT si elle a été modifiée par un re-render
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || !editMode) return

    // Petit délai pour laisser le DOM se mettre à jour
    const timeoutId = setTimeout(() => {
      if (isUserScrollingRef.current && scrollPositionRef.current !== container.scrollTop) {
        container.scrollTop = scrollPositionRef.current
      }
      isUserScrollingRef.current = false
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [dataSet, editMode])

  // --- 1. SYNCHRONISATION : PARENT -> ENFANT ---
  useEffect(() => {
    if (data && field?.name) {
      const parentRows = data[field.name] || []

      // Initialisation au premier montage SI on a des données parent
      if (!initializedRef.current && parentRows.length > 0) {
        console.log('MyTableEditable: INITIALIZATION from parent', parentRows)
        setDataSet(parentRows)
        initializedRef.current = true
        return
      }

      // 🔴 BLOCAGE CRITIQUE: Ne RIEN faire pendant l'édition
      if (editMode || isSubmit || isJustSaved) {
        return
      }

      // 🔴 BLOCAGE: Ne pas écraser si l'utilisateur a modifié localement
      if (!isPristineRef.current) {
        console.log('MyTableEditable: SKIP update from parent (form is dirty)')
        return
      }

      // Comparaison profonde uniquement si conditions OK
      const stringifiedParent = JSON.stringify(parentRows)
      const stringifiedLocal = JSON.stringify(dataSet)

      if (initializedRef.current && stringifiedParent !== stringifiedLocal) {
        console.log('MyTableEditable: UPDATE from parent (pristine)')
        setDataSet(parentRows)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.[field?.name], editMode, isSubmit, isJustSaved])
  useEffect(() => {
    if (isJustSaved) {
      setIsJustSaved(false)
    }
  }, [data?.[field?.name]])
  // --- 2. SYNCHRONISATION : ENFANT -> PARENT (avec debounce intelligent) ---
  useEffect(() => {
    // NE PAS synchroniser pendant la sauvegarde
    /*if (isSubmit || isJustSaved) {
      return
    }*/

    if (isSubmit || isJustSaved || editMode) return

    // 🔴 NE PAS synchroniser pendant l'édition active
    // Cela évite de re-render le formulaire parent (et donc JoditEditor, etc.)
    if (editMode) {
      return
    }

    if (setData && initializedRef.current) {
      const parentRows = data?.[field?.name] || []

      // 🔴 FILTRAGE: Retirer les lignes vides avant la synchronisation
      const filteredDataSet = dataSet.filter((row) => !isEmptyRow(row))

      const stringifiedParent = JSON.stringify(parentRows)
      const stringifiedLocal = JSON.stringify(filteredDataSet)

      if (stringifiedParent !== stringifiedLocal) {
        // Annuler la synchronisation précédente
        if (syncTimeoutRef.current) {
          clearTimeout(syncTimeoutRef.current)
        }

        console.debug('MyTableEditable: scheduling sync child->parent (not editing)')

        syncTimeoutRef.current = setTimeout(() => {
          const freshFiltered = dataSetRef.current.filter((row) => !isEmptyRow(row))
          // Vérifier une dernière fois pour éviter les boucles infinies
          // (ex: SuperAdmin où data est recalculé à chaque changement)
          const currentParentStr = JSON.stringify(dataRef.current?.[field?.name] || [])
          const freshStr = JSON.stringify(freshFiltered)
          if (currentParentStr !== freshStr) {
            setData(freshFiltered)
          }
          syncTimeoutRef.current = null
        }, 0)
      }
    }

    // Cleanup
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSet, field.name, isSubmit, isJustSaved, isEmptyRow, editMode])

  // Relâcher isJustSaved quand le parent a bien reçu les nouvelles données
  useEffect(() => {
    if (isJustSaved) setIsJustSaved(false)
  }, [data?.[field?.name]])

  // Sécurité : relâcher isJustSaved après 500ms si le parent ne se met pas à jour
  useEffect(() => {
    if (!isJustSaved) return
    const t = setTimeout(() => setIsJustSaved(false), 500)
    return () => clearTimeout(t)
  }, [isJustSaved])

  useEffect(() => {
    setPage(activePage)
  }, [activePage])

  // --- GESTION DES CHANGEMENTS ---
  const onValueChange = useCallback(
    (e) => {
      const { name, type, checked, value } = e.target
      const newValue = type === 'checkbox' ? checked : value

      // Marquer comme modifié
      isPristineRef.current = false

      setEditingRow((prev) => {
        let updatedRow = { ...prev, [name]: newValue }
        tableOptions.columns.forEach((col) => {
          if (col.defaultValueFunction) {
            const calc = col.defaultValueFunction(updatedRow)
            if (calc !== undefined) updatedRow[col.name] = calc
          }
        })
        return updatedRow
      })
      setIsChanged(true)
    },
    [tableOptions.columns]
  )

  const onDateChange = useCallback(
    (name) => (newValue) => {
      isPristineRef.current = false

      setEditingRow((prev) => {
        let updatedRow = { ...prev, [name]: newValue }
        tableOptions.columns.forEach((col) => {
          if (col.defaultValueFunction) {
            const calc = col.defaultValueFunction(updatedRow)
            if (calc !== undefined) updatedRow[col.name] = calc
          }
        })
        return updatedRow
      })
      setIsChanged(true)
    },
    [tableOptions.columns]
  )

  const handleChangeFile = useCallback(
    (field) => async (event) => {
      const files = Array.from(event.target.files)
      if (!files.length) return

      isPristineRef.current = false

      const existingImageId = editingRow?.attachments?.find((a) => a.type === 'image' && !a.isPreview)?.id

      const newPreviews = files.map((f) => ({
        id: existingImageId,
        type: 'image',
        file: f,
        isPreview: true
      }))

      setEditingRow((prev) => {
        const currentAttachments = prev[field.name] || []
        const otherFiles = currentAttachments.filter((a) => a.type !== 'image')
        return { ...prev, [field.name]: [...otherFiles, ...newPreviews] }
      })
      setIsChanged(true)
    },
    [editingRow]
  )

  // --- ACTIONS ---
  const validate = async () => {
    if (!childRef.current) return false
    const refs = childRef.current.filter((ref) => ref?.validateField)
    const promises = refs.map((ref) => {
      try {
        return ref.validateField()
      } catch (e) {
        return Promise.resolve('validation error')
      }
    })
    const results = await Promise.all(promises)
    const hasError = results.some((res) => res !== null)
    if (hasError) {
      console.warn('MyTableEditable validation errors:', results)
    }
    return hasError
  }

  const save = async () => {
    const hasErr = await validate()
    if (hasErr) {
      toast.error('La ligne contient des champs invalides. Vérifiez et réessayez.', { autoClose: 3000 })
      return
    }

    // 🔴 Vérification supplémentaire: empêcher la sauvegarde de lignes vides
    if (isEmptyRow(editingRow)) {
      toast.warning('Impossible de sauvegarder une ligne vide.', { autoClose: 2000 })
      return
    }

    // 🟡 Forcer la synchronisation immédiate avant la sauvegarde
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current)
      syncTimeoutRef.current = null
    }

    setIsSubmit(true)
  }

  const add = useCallback(() => {
    const id = Date.now()

    // 🔴 Créer une ligne avec des valeurs par défaut selon les colonnes définies
    const newRow = { id_tmp: id }
    tableOptions.columns.forEach((col) => {
      if (col.defaultValue !== undefined) {
        if (col.defaultValue()) newRow[col.name] = col.defaultValue()
        else newRow[col.name] = col.defaultValue
      } /* else if (col.type === 'text' || col.type === 'text-multiline') {
        newRow[col.name] = ''
      } else if (col.type === 'number' || col.type === 'currency' || col.type === 'float') {
        newRow[col.name] = 0
      } else if (col.type === 'checkbox') {
        newRow[col.name] = false
      }*/
    })

    isPristineRef.current = false

    const newDataSet = [...dataSet, newRow]
    setDataSet(newDataSet)

    // 🟢 SYNC IMMÉDIATE lors de l'ajout (avant l'édition)
    if (setData) {
      // Forcer la synchronisation immédiate
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
        syncTimeoutRef.current = null
      }

      console.log('MyTableEditable.add: calling setData')
      //setData((prev) => ({ ...(prev || {}), [field.name]: newDataSet }))
      //setData(newDataSet)
    }

    opVersionRef.current += 1

    setPreviousRow({})
    setEditingRow(newRow)
    setRowId(id)
    setEditMode(true) // Activer le mode édition APRÈS la sync
    setPage(Math.floor(newDataSet.length / rowsPerPage))
  }, [dataSet, setData, field.name, rowsPerPage, tableOptions.columns])

  const cancel = useCallback(() => {
    const oldData = [...dataSet]
    const idx = oldData.findIndex((obj) => obj.id === rowId || obj.id_tmp === rowId)
    if (idx !== -1) {
      if (Object.keys(previousRow).length === 0) {
        // Nouvelle ligne non sauvegardée -> suppression
        oldData.splice(idx, 1)
      } else {
        // Restauration de la ligne précédente
        oldData[idx] = previousRow
      }
    }

    // 🔴 Filtrer les lignes vides après annulation
    const filtered = oldData.filter((row) => !isEmptyRow(row))

    setDataSet(filtered)
    opVersionRef.current += 1

    setEditMode(false)
    setRowId(null)
    setEditingRow({})

    // Synchroniser avec le parent
    if (setData) {
      setTimeout(() => {
        //setData((prev) => ({ ...(prev || {}), [field.name]: filtered }))
        setData(filtered)
      }, 0)
    }

    // Restaurer pristine si on annule une nouvelle ligne
    if (Object.keys(previousRow).length === 0) {
      isPristineRef.current = true
    }
  }, [dataSet, rowId, previousRow, isEmptyRow, setData, field.name])

  const deleteRow = useCallback(
    async (id) => {
      const oldData = [...dataSet]
      const idx = oldData.findIndex((obj) => obj.id === id || obj.id_tmp === id)
      if (idx === -1) return

      isPristineRef.current = false

      if (tableOptions.object && oldData[idx].id) {
        await deleteObject(tableOptions.object, oldData[idx].id)
      }
      oldData.splice(idx, 1)
      setDataSet(oldData)

      // 🟢 SYNC IMMÉDIATE lors de la suppression
      if (setData) {
        // Forcer la synchronisation immédiate
        if (syncTimeoutRef.current) {
          clearTimeout(syncTimeoutRef.current)
          syncTimeoutRef.current = null
        }

        console.log('MyTableEditable.deleteRow: calling setData')
        //setData((prev) => ({ ...(prev || {}), [field.name]: oldData }))
        setData(oldData)
      }
      setEditMode(false)
    },
    [dataSet, tableOptions.object, setData, field.name]
  )

  // --- EFFET DE SAUVEGARDE (API + SYNC) ---
  useEffect(() => {
    if (!isSubmit) return

    const runSave = async () => {
      const hasError = await validate()
      if (hasError || isEmptyRow(editingRow)) {
        cancel()
        setIsSubmit(false)
        return
      }

      const version = opVersionRef.current
      try {
        let rowToSave = { ...editingRow }

        // --- ÉTAPE 1 : Sauvegarde initiale (Création ou Edit) ---
        // Cette étape permet de récupérer un ID réel du serveur si c'est une nouvelle ligne
        const res = tableOptions.object
          ? await (rowToSave.id
              ? editObject(tableOptions.object, rowToSave.id, rowToSave)
              : addObject(tableOptions.object, rowToSave))
          : { data: rowToSave }

        let savedData = res.data

        // Fusionner les attachments : conserver les previews du front (isPreview=true)
        // que le serveur ne connaît pas encore, avec les attachments existants du serveur.
        const frontAttachments = rowToSave.attachments || []
        const frontPreviews = frontAttachments.filter((a) => a.isPreview === true || a.file instanceof File)
        if (!savedData.attachments) {
          savedData.attachments = frontAttachments
        } else if (frontPreviews.length > 0) {
          // Garder les attachments serveur + ajouter les nouveaux previews du front
          const serverNonPreview = savedData.attachments.filter((a) => !a.isPreview)
          savedData.attachments = [...serverNonPreview, ...frontPreviews]
        }

        // --- ÉTAPE 2 : Upload des images (Maintenant savedData.id est garanti) ---
        if (tableOptions.object && savedData.attachments.length > 0) {
          const toUpload = savedData.attachments.filter((a) => a.type === 'image' && a.isPreview)

          if (toUpload.length) {
            // handleImagesUpload utilisera l'id de savedData pour le stockage physique
            const uploaded = await handleImagesUpload(
              toUpload,
              user,
              savedData,
              null,
              tableOptions.options?.fileParentType
            )

            // Remplacement des previews par les données réelles du serveur (url, path, etc.)
            // Remplacer chaque preview par l'objet serveur correspondant (match par index)
            let uploadIdx = 0
            savedData.attachments = savedData.attachments.map((a) => {
              if (a.type === 'image' && a.isPreview && uploadIdx < uploaded.length) {
                return { ...uploaded[uploadIdx++], isPreview: false }
              }
              return a
            })

            // Étape 3 supprimée : l'API persiste déjà les filenames lors de l'upload.
          }
        }

        // --- ÉTAPE 4 : Cache-busting des URLs pour forcer le rafraîchissement ---
        if (savedData.attachments && Array.isArray(savedData.attachments)) {
          savedData.attachments = savedData.attachments.map((a) =>
            a.type === 'image' && a.url
              ? {
                  ...a,
                  url: `${a.url}${a.url.includes('?') ? '&' : '?'}t=${Date.now()}`,
                  isPreview: false
                }
              : a
          )
        }

        // --- ÉTAPE 5 : Mise à jour des états locaux (DataSet et Parent) ---
        setDataSet((prev) => {
          const next = prev.map((r) => {
            const matchById = savedData.id != null && r.id === savedData.id
            const matchByTmp = r.id_tmp === rowId
            return matchById || matchByTmp ? savedData : r
          })
          dataSetRef.current = next
          return next
        })

        if (setData && version === opVersionRef.current) {
          // setTimeout pour garantir que la pile d'exécution React est prête
          setTimeout(() => {
            setData(() => {
              const currentDataSet = dataSetRef.current.filter((row) => !isEmptyRow(row))
              // Si on travaille sur une liste, on remplace l'élément correspondant
              return currentDataSet
            })
          }, 0)
        }

        // Reset du mode édition
        setEditMode(false)
        setRowId(null)
        setEditingRow({})
        setIsChanged(false)
      } catch (err) {
        console.error('[runSave Error]', err)
        toast.error('Erreur lors de la sauvegarde de la ligne', { autoClose: 3000 })
      } finally {
        setIsSubmit(false)
      }
    }

    runSave()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmit])

  const isRowEditing = (row) => (row.id === rowId || row.id_tmp === rowId) && editMode

  const renderRows = () => {
    childRef.current = []
    return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, i) => {
      return (
        <MyTableEditableRow
          key={row.id_tmp ?? row.id ?? i}
          row={row}
          mode={mode}
          tableOptions={tableOptions}
          editMode={editMode}
          rowid={rowId}
          editingRow={editingRow}
          onValueChange={onValueChange}
          onDateChange={onDateChange}
          setEditingRow={setEditingRow}
          handleChangeFile={handleChangeFile}
          childRef={childRef}
          isRowEditing={isRowEditing}
          setEditMode={setEditMode}
          setRowId={setRowId}
          setPreviousRow={setPreviousRow}
          save={save}
          deleteRow={deleteRow}
          cancel={cancel}
          rowBgColor={i % 2 !== 0 ? ODD_ROW_COLOR : WHITE_COLOR}
        />
      )
    })
  }

  return (
    <>
      <Box sx={{ ...paperStyle, display: 'flex', flexDirection: 'row-reverse', mb: 1 }}>
        {mode === 'edit' && tableOptions.actions?.add && (
          <Tooltip title='Ajouter'>
            <span>
              <IconButton onClick={add} disabled={editMode} color='primary'>
                <Add />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <Paper
        variant='outlined'
        style={{ ...paperStyle, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
      >
        <TableContainer ref={scrollContainerRef} style={{ ...tableStyle, flex: 1 }}>
          <Table stickyHeader sx={{ tableLayout: 'fixed', minWidth: '100%' }}>
            <TableHead>
              <TableRow sx={headerStyle}>
                {tableOptions.isExpand && <TableCell padding='checkbox' sx={headerStyle} />}
                {tableOptions.columns.map(
                  (column, i) =>
                    column.display !== false && (
                      <MyTableCellHeader
                        key={i}
                        header={column}
                        sort={handleSort}
                        sortedColumn={sortedColumn}
                        sortedColumnMode={sortedColumnMode}
                      />
                    )
                )}
                {tableOptions.actions && mode === 'edit' && (
                  <MyTableCellHeader header={{ label: 'Actions', isSorting: false }} resize={false} />
                )}
              </TableRow>
            </TableHead>
            <TableBody>{renderRows()}</TableBody>
          </Table>
        </TableContainer>

        {tableOptions.showTotal?.display && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e0e0e0' }}>
            <strong>{tableOptions.showTotal.label} : </strong>
            <span style={{ marginLeft: '10px' }}>
              {dataSet.reduce((sum, row) => sum + (parseFloat(row[tableOptions.showTotal.field]) || 0), 0).toFixed(2)}
              {tableOptions.showTotal.type === 'currency' ? ' €' : ''}
            </span>
          </Box>
        )}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={dataSet.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value)
            setPage(0)
          }}
          ActionsComponent={MyTablePaginationActions}
        />
      </Paper>
    </>
  )
}

export default MyTableEditable
